import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Activity, Link as LinkIcon, RefreshCw, Plus, Copy, Trash2, Globe } from "lucide-react";
import SpeakerButton from "./SpeakerButton";
import {
  pingTelemetry, fetchTelemetry, fetchLinks, createLink, deleteLink, API
} from "../lib/api";

const StatusLight = ({ status }) => {
  const color = status === "online" ? "bg-green-400" : status === "offline" ? "bg-red-500" : "bg-white/30";
  const ring = status === "online" ? "shadow-[0_0_18px_rgba(74,222,128,0.65)]" : status === "offline" ? "shadow-[0_0_18px_rgba(239,68,68,0.65)]" : "";
  return (
    <span className="relative inline-flex h-3.5 w-3.5">
      <span className={`absolute inset-0 rounded-full ${color} opacity-70 eigoo-blink`} />
      <span className={`relative rounded-full h-3.5 w-3.5 ${color} ${ring}`} />
    </span>
  );
};

export const StationTelemetry = () => {
  const [telemetry, setTelemetry] = useState({ status: "unknown", deployment_url: "", code: 0 });
  const [url, setUrl] = useState("");
  const [pinging, setPinging] = useState(false);
  const [links, setLinks] = useState([]);
  const [newLink, setNewLink] = useState({ target_url: "", label: "" });
  const [creating, setCreating] = useState(false);

  const refresh = async () => {
    const t = await fetchTelemetry().catch(() => null);
    if (t) { setTelemetry(t); if (t.deployment_url) setUrl(t.deployment_url); }
    const l = await fetchLinks().catch(() => []);
    setLinks(l || []);
  };

  useEffect(() => { refresh(); }, []);

  const ping = async () => {
    if (!url.trim()) return;
    setPinging(true);
    try {
      const r = await pingTelemetry(url.trim());
      setTelemetry({ status: r.status, deployment_url: r.url, code: r.code });
    } finally { setPinging(false); }
  };

  const addLink = async (e) => {
    e.preventDefault();
    if (!newLink.target_url.trim()) return;
    setCreating(true);
    try {
      const created = await createLink(newLink);
      setLinks([created, ...links]);
      setNewLink({ target_url: "", label: "" });
    } finally { setCreating(false); }
  };

  const removeLink = async (slug) => {
    await deleteLink(slug);
    setLinks(links.filter(l => l.slug !== slug));
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
  };

  const totalClicks = links.reduce((s, l) => s + (l.clicks || 0), 0);

  const linkBase = `${API}/r/`;

  return (
    <div className="space-y-6" data-testid="station-telemetry">
      {/* Vercel deployment status */}
      <div className="eigoo-glass rounded-3xl p-6">
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-2xl bg-white/5 border border-yellow-300/25 flex items-center justify-center">
              <Activity className="text-yellow-300" size={22} />
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-yellow-300/80 font-display">Vercel Deployment</div>
              <div className="font-display font-bold text-xl">Fleet Health</div>
            </div>
            <SpeakerButton
              text={`Deployment status is ${telemetry.status === "online" ? "online and healthy" : telemetry.status === "offline" ? "offline. Check your fleet" : "not yet checked"}.`}
              testid="speaker-telemetry"
              size="sm"
            />
          </div>
          <motion.div
            key={telemetry.status}
            initial={{ scale: 0.85 }}
            animate={{ scale: 1 }}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full border ${
              telemetry.status === "online"
                ? "bg-green-500/10 border-green-500/40 text-green-300"
                : telemetry.status === "offline"
                ? "bg-red-500/10 border-red-500/40 text-red-300"
                : "bg-white/5 border-white/10 text-white/60"
            }`}
            data-testid="vercel-status-pill"
          >
            <StatusLight status={telemetry.status} />
            <span className="font-display font-bold text-sm uppercase tracking-widest">{telemetry.status}</span>
          </motion.div>
        </div>

        <div className="flex gap-2">
          <input
            value={url}
            onChange={e => setUrl(e.target.value)}
            placeholder="https://your-app.vercel.app"
            data-testid="vercel-url-input"
            className="flex-1 px-4 py-3 rounded-2xl bg-black/40 border border-yellow-300/20 focus:border-yellow-300/60 outline-none"
          />
          <button onClick={ping} disabled={pinging} className="btn-eigoo btn-ghost-gold" data-testid="ping-deployment">
            <RefreshCw size={18} className={pinging ? "animate-spin" : ""} />
            Ping
          </button>
        </div>
        {telemetry.code > 0 && (
          <div className="text-xs text-white/40 mt-2">HTTP {telemetry.code} · last checked just now</div>
        )}
      </div>

      {/* Link Clicks */}
      <div className="eigoo-glass rounded-3xl p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-2xl bg-white/5 border border-red-500/30 flex items-center justify-center">
              <LinkIcon className="text-red-400" size={22} />
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-red-300/80 font-display">Link Clicks</div>
              <div className="font-display font-bold text-xl">Signal Tracker</div>
            </div>
            <SpeakerButton
              text={`Your fleet links have a total of ${totalClicks} clicks.`}
              testid="speaker-links"
              size="sm"
            />
          </div>
          <div className="px-4 py-2 rounded-full bg-red-500/10 border border-red-500/30 text-red-200 font-display font-bold" data-testid="total-clicks">
            {totalClicks} <span className="text-xs font-normal text-red-200/70">total clicks</span>
          </div>
        </div>

        <form onSubmit={addLink} className="grid md:grid-cols-3 gap-2 mb-4" data-testid="create-link-form">
          <input
            value={newLink.label}
            onChange={e => setNewLink({ ...newLink, label: e.target.value })}
            placeholder="Label (e.g. My Site)"
            className="px-4 py-3 rounded-2xl bg-black/40 border border-yellow-300/20 outline-none"
            data-testid="link-label-input"
          />
          <input
            value={newLink.target_url}
            onChange={e => setNewLink({ ...newLink, target_url: e.target.value })}
            placeholder="https://example.com"
            className="px-4 py-3 rounded-2xl bg-black/40 border border-yellow-300/20 outline-none md:col-span-1"
            data-testid="link-url-input"
          />
          <button disabled={creating} className="btn-eigoo btn-red" data-testid="submit-link">
            <Plus size={18} /> {creating ? "Adding..." : "Add Link"}
          </button>
        </form>

        {links.length === 0 ? (
          <div className="text-center text-white/50 py-6" data-testid="links-empty">No tracked links yet.</div>
        ) : (
          <ul className="divide-y divide-white/5">
            {links.map(l => (
              <li key={l.slug} className="py-3 flex items-center gap-3 flex-wrap" data-testid={`link-row-${l.slug}`}>
                <Globe className="text-yellow-300 shrink-0" size={18} />
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold truncate">{l.label || l.target_url}</div>
                  <div className="text-xs text-white/50 truncate">{l.target_url}</div>
                  <div className="text-[11px] text-yellow-300/80 truncate">{linkBase}{l.slug}</div>
                </div>
                <div className="px-3 py-1 rounded-full bg-yellow-300/10 border border-yellow-300/30 text-yellow-200 font-display font-bold text-sm">
                  {l.clicks || 0}
                </div>
                <button onClick={() => copy(`${linkBase}${l.slug}`)} className="hit-target px-3 rounded-xl bg-white/5 hover:bg-yellow-300/15 border border-white/10" data-testid={`copy-link-${l.slug}`}>
                  <Copy size={16} />
                </button>
                <button onClick={() => removeLink(l.slug)} className="hit-target px-3 rounded-xl bg-white/5 hover:bg-red-500/20 hover:text-red-300 border border-white/10" data-testid={`delete-link-${l.slug}`}>
                  <Trash2 size={16} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default StationTelemetry;
